'use client';

import { useTranslations } from 'next-intl';
import { Image } from 'components/image';
import { replaceTokensWithComponents } from '@cat-ecom/common/utils/stringUtils';
import { camelCase, startCase } from 'lodash';
import {
  CatButton,
  CatCheckboxField,
  CatHeading
} from 'components/blocks-components';
import type { SelectedEquipmentInfo as SelectedEquipmentInfoType } from 'app/[lang]/[esite]/(core)/_header/components/equipment-widget/types';
import { useMyEquipmentDrawer } from 'components/my-equipment/my-equipment-drawer-context';
import { useToggleFilterByEquipment } from '../../common';
import { Conditional, TextLink } from '@cat-ecom/pcc-components';
import cx from 'classnames';
import styles from './selected-equipment.module.scss';

const SelectedEquipmentWrapper = ({
  selectedEquipmentInfo,
  shouldTriggerNER
}: {
  selectedEquipmentInfo: SelectedEquipmentInfoType;
  shouldTriggerNER: boolean;
}) => {
  const t = useTranslations();
  const imgURL = selectedEquipmentInfo.imageURL + '?wid=60&hei=60&fmt=webp';
  const equipmentName = `${selectedEquipmentInfo.model} ${startCase(
    camelCase(selectedEquipmentInfo.productFamily)
  )}`;

  const checkBoxLabel = replaceTokensWithComponents(
    t('REFINE_RESULTS_FOR', { '0': '{0}' }),
    <strong>{selectedEquipmentInfo.model}</strong>
  );

  const { handleCheckboxChange, filterByEquipment } =
    useToggleFilterByEquipment(selectedEquipmentInfo);
  const {
    editEquipmentDrawer: { openDrawer: handleOpenEditEquipmentDrawer },
    selectEquipmentDrawer: { openDrawer: handleOpenSelectEquipmentDrawer }
  } = useMyEquipmentDrawer();

  const shouldFilterByEquipment = !shouldTriggerNER && filterByEquipment;

  const addSerialLink = replaceTokensWithComponents(
    t('MEQ_TO_GET_AN_EXACT_FIT', { '0': '{0}' }),
    <TextLink
      label={t('MEQ_ADD_SERIAL_NUMBER')}
      onClick={handleOpenEditEquipmentDrawer}
      className="text-decoration-underline"
    />
  );

  const selectedEquipmentShowInfo = (
    <>
      <CatHeading
        level="h3"
        variant="title-sm"
        className={styles['selected-equipment-wrapper__title']}
      >
        {selectedEquipmentInfo.assetId
          ? selectedEquipmentInfo.assetId
          : equipmentName}
      </CatHeading>
      <Conditional test={!!selectedEquipmentInfo.assetId}>
        <CatHeading
          level="h4"
          variant="footnote"
          className={cx(styles['selected-equipment-wrapper__footnote'])}
        >
          {equipmentName}
        </CatHeading>
      </Conditional>

      {selectedEquipmentInfo.serialNumber && (
        <CatHeading
          level="h4"
          variant="footnote"
          className={cx(styles['selected-equipment-wrapper__footnote'])}
        >
          {selectedEquipmentInfo.serialNumber}
        </CatHeading>
      )}
    </>
  );

  const selectedEquipmentImage = (
    <Image
      src={imgURL}
      width={64}
      height={64}
      loading="lazy"
      alt={equipmentName}
      className={cx('me-2', styles['selected-equipment-wrapper__image'])}
      unoptimized
    />
  );

  const changeButton = (
    <CatButton
      variant="inline"
      className={styles['selected-equipment-wrapper__change-button']}
      onClick={handleOpenSelectEquipmentDrawer}
    >
      {t('CHANGE')}
    </CatButton>
  );

  const addSerialText = (
    <CatHeading
      level="h4"
      variant="footnote"
      className={cx('mt-2', styles['selected-equipment-wrapper__footnote'])}
    >
      {addSerialLink}
    </CatHeading>
  );

  return (
    <div className="d-flex flex-column">
      <div className="d-flex flex-row d-md-none d-lg-flex align-items-center pb-3">
        {selectedEquipmentImage}
        <div
          className={cx(
            'd-flex flex-column ms-1 w-100',
            styles['selected-equipment-wrapper__name-container'],
            styles['selected-equipment-wrapper__name-container--desktop']
          )}
        >
          <div
            className={cx(
              'd-flex d-flex align-items-start align-items-center justify-content-between w-100',
              styles['selected-equipment-wrapper__name-container--mobile']
            )}
          >
            <h4
              className={cx(
                'cat-u-theme-typography-title-sm mb-0',
                styles['selected-equipment-wrapper__equipment-name']
              )}
            >
              {selectedEquipmentInfo.assetId
                ? selectedEquipmentInfo.assetId
                : equipmentName}
            </h4>
            {changeButton}
          </div>
          <Conditional test={!!selectedEquipmentInfo.assetId}>
            <CatHeading
              level="h4"
              variant="footnote"
              className={cx(styles['selected-equipment-wrapper__footnote'])}
            >
              {equipmentName}
            </CatHeading>
          </Conditional>
          {selectedEquipmentInfo.serialNumber && (
            <CatHeading
              level="h4"
              variant="footnote"
              className={cx(styles['selected-equipment-wrapper__footnote'])}
            >
              {selectedEquipmentInfo.serialNumber}
            </CatHeading>
          )}
          {!selectedEquipmentInfo.serialNumber && addSerialText}
        </div>
      </div>
      <div className="d-none d-md-flex d-lg-none flex-column pb-2">
        <div className="d-flex justify-content-between align-items-start pb-1">
          {selectedEquipmentImage}
          {changeButton}
        </div>
        <div className="d-flex flex-column ms-1">
          {selectedEquipmentShowInfo}
          {!selectedEquipmentInfo.serialNumber && addSerialText}
        </div>
      </div>
      <CatCheckboxField
        data-testid="filter-by-equipment-checkbox"
        className={styles['selected-equipment-wrapper__checkbox']}
        checked={shouldFilterByEquipment}
        onBlCheckboxChange={handleCheckboxChange}
      >
        {checkBoxLabel}
      </CatCheckboxField>
    </div>
  );
};
export default SelectedEquipmentWrapper;
